import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  optionsSelector,
  userSelector,
  usersSelector,
  totalSelector,
  pageSelector,
  noMoreSelector,
} from './store/selector';
import { usersApiService } from './services/api';
import {
  userClearAction,
  getUserFailureApiAction,
  getUserInitApiAction,
  getUserSuccessApiAction,
  getUsersFailureApiAction,
  getUsersInitApiAction,
  getUsersSuccessApiAction,
  putUserFailuireApiAction,
  putUserInitApiAction,
  putUserSuccessApiAction,
  setOptionsAction,
  resetOptionsAction,
  postUserInitApiAction,
  postUserSuccessApiAction,
  postUserFailuireApiAction,
  increasePageAction,
  deleteUserInitApiAction,
  deleteUserSuccessApiAction,
  deleteUserFailuireApiAction,
  setUserAction,
} from './store/reducer';
import { GetUsersParams } from './interfaces/get-users';
import { PutUserPayloadRequest } from './interfaces/put-user';
import { PostUserPayloadRequest } from './interfaces/post-user';
import { User } from '../../interfaces/user.interface';

export function useUserCore() {
  const users = useSelector(usersSelector);
  const user = useSelector(userSelector);
  const total = useSelector(totalSelector);
  const page = useSelector(pageSelector);
  const noMore = useSelector(noMoreSelector);
  const options = useSelector(optionsSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getUsers = useCallback(
    async (params: GetUsersParams, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(getUsersInitApiAction());
        const response = await usersApiService.getUsers(params);
        dispatch(getUsersSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(getUsersFailureApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const getUser = useCallback(
    async (id: number, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(getUserInitApiAction());
        const response = await usersApiService.getUser(id);
        dispatch(getUserSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(getUserFailureApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const postUser = useCallback(
    async (
      body: PostUserPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postUserInitApiAction());
        await usersApiService.postUser(body);
        dispatch(postUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(postUserFailuireApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const putUser = useCallback(
    async (
      id: number,
      body: PutUserPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putUserInitApiAction());
        await usersApiService.putUser(id, body);
        dispatch(putUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(putUserFailuireApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const deleteUser = useCallback(
    async (id: number, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(deleteUserInitApiAction());
        await usersApiService.deleteUser(id);
        dispatch(deleteUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(deleteUserFailuireApiAction());
      }
    },
    [dispatch]
  );

  const setUser = useCallback((user: User) => {
    dispatch(setUserAction(user));
  }, [dispatch]);

  const setOptions = useCallback((options: GetUsersParams) => {
    dispatch(setOptionsAction(options));
  }, [dispatch]);

  const resetOptions = useCallback(() => {
    dispatch(resetOptionsAction());
  }, [dispatch]);

  const increasePage = useCallback(() => {
    dispatch(increasePageAction());
  }, [dispatch]);

  const clearUser = useCallback(() => {
    dispatch(userClearAction());
  }, [dispatch]);

  return {
    users,
    user,
    options,
    total,
    page,
    noMore,
    errorCode,
    getUsers,
    getUser,
    postUser,
    putUser,
    deleteUser,
    setUser,
    setOptions,
    increasePage,
    resetOptions,
    clearUser,
  };
}
